import { Fragment, ReactElement } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconReadMore } from '@components/icons/read-more';
import { IconTeam } from '@components/icons/team';
import { ProgressBar } from '@components/progress-bar';
import { CloseButton, Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { SelectionStore, createSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { useCurrentCompany } from 'api/authentication/hooks';
import { StepKey } from 'api/authentication/requests';
import {
  useCompleteOnboarding,
  useCompleteOnboardingStep,
  useSkipOnboarding,
} from 'api/company/hooks';
import { config } from 'app-config';

import { useCreateJourneyModal } from '../main/pages/journeys/modals/create-journey';
import { routePaths } from '../navigation';

export const useCompanyOnboardingSidebar = ({ onHide }: { onHide: () => void }) => {
  return useSidebar(CompanyOnboardingSidebar, {
    variant: 'REGULAR',
    backgroundColor: 'gradient',
    closeOnClickOutside: true,
    onHide,
  });
};

type StepType = {
  name: StepKey;
  label: string;
  description?: string;
  isCompleted: boolean;
  link?: string;
  isLinkExternal?: boolean;
  onClick?: () => void;
  completeStep?: () => void;
  buttonLabel?: string;
  buttonIcon?: ReactElement<IconProps>;
};

const getExpandedStore = (persistKey: string) => {
  return createSelectionStore({
    persistKey: persistKey,
    defaultState: {
      runDemoJourney: false,
      explorePortal: true,
      exploreTemplates: true,
      publishJourney: true,
      inviteTeammate: true,
    },
  });
};

const CompanyOnboardingSidebar = () => {
  const { t } = useScopedTranslate(locale);
  const company = useCurrentCompany();
  const skipOnboarding = useSkipOnboarding();
  const completeOnboarding = useCompleteOnboarding();
  const expandedStore = getExpandedStore(`company-onboarding-state-${company?.shortId}`);
  const handleSkipOnboarding = async () => {
    skipOnboarding.mutateAsync();
    hideSidebar();
  };
  const handleCompleteOnboarding = async () => {
    completeOnboarding.mutateAsync();
    hideSidebar();
  };
  const completeOnboardingStep = useCompleteOnboardingStep();
  const showCreateJourneyModal = useCreateJourneyModal();

  const portalDomain = company?.portalDomain
    ? `https://${company.portalDomain}`
    : `${config.PORTAL_URL}/${company?.slug}/`;

  const onboardingStatus = company?.onboarding?.status;
  const onboardingSteps = onboardingStatus?.steps;

  const journeyTravellerPath = generatePath(routePaths.traveller, {
    travellerId: onboardingStatus?.config.travellerJourneyId ?? '',
    journeyId: onboardingStatus?.config.journeyId ?? '',
    workspaceId: onboardingStatus?.config.homeWorkspaceId ?? '',
    '*': null,
  });

  const steps: StepType[] = [
    {
      name: 'createAccount',
      label: t('createAnAccount'),
      isCompleted: true,
    },
    {
      name: 'runDemoJourney',
      label: t('runDemoJourney'),
      description: t('runDemoJourneyDescription'),
      isCompleted: onboardingSteps?.runDemoJourney ?? false,
      link: journeyTravellerPath,
      isLinkExternal: false,
      buttonLabel: t('runDemoJourneyButtonLabel'),
      buttonIcon: <IconJourneyItem />,
    },
    {
      name: 'explorePortal',
      label: t('explorePortal'),
      description: t('explorePortalDescription'),
      isCompleted: onboardingSteps?.explorePortal ?? false,
      link: portalDomain,
      isLinkExternal: true,
      buttonLabel: t('explorePortalButtonLabel'),
      completeStep: () => completeOnboardingStep('explorePortal'),
    },
    {
      name: 'exploreTemplates',
      label: t('exploreTemplates'),
      description: t('exploreTemplatesDescription'),
      isCompleted: onboardingSteps?.exploreTemplates ?? false,
      link: 'https://www.50skills.com/templates',
      isLinkExternal: true,
      buttonLabel: t('exploreTemplatesButtonLabel'),
      completeStep: () => completeOnboardingStep('exploreTemplates'),
    },
    {
      name: 'publishJourney',
      label: t('publishJourney'),
      description: t('publishJourneyDescription'),
      isCompleted: onboardingSteps?.publishJourney ?? false,
      onClick: () => showCreateJourneyModal(),
      buttonLabel: t('publishJourneyButtonLabel'),
      buttonIcon: <IconAddPlus />,
    },
    {
      name: 'inviteTeammate',
      label: t('inviteTeammate'),
      description: t('inviteTeammateDescription'),
      isCompleted: onboardingSteps?.inviteTeammate ?? false,
      link: generatePath(routePaths.settingsUsers),
      isLinkExternal: false,
      buttonLabel: t('inviteTeammateButtonLabel'),
      buttonIcon: <IconTeam />,
    },
  ];

  const ratio = company?.onboarding?.ratio ?? 0;

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" space={2}>
            <IconReadMore color="elementIndigo100" />
            <Text font="bold18" color="textPrimary" text={t('gettingStarted')} />
          </Stack>
          <CloseButton />
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={4}>
          <Text font="regular14" color="textSecondary" text={t('welcome')} />
          <ProgressBar variant="info" ratio={ratio} />
          <Sidebar.Separator />
          {steps.map((step, index) => (
            <Fragment key={step.name}>
              <OnboardingItem step={step} id={index} expandedStore={expandedStore} />
              <Sidebar.Separator />
            </Fragment>
          ))}
          {ratio < 1 ? (
            <Stack orientation="horizontal" justify="flex-end">
              <Button
                variant="secondary"
                text={t('skipButtonLabel')}
                onClick={handleSkipOnboarding}
                iconLeft={<IconCheckmark />}
              />
            </Stack>
          ) : (
            <Stack space={4}>
              <Text font="regular14" color="textSecondary" text={t('allStepsCompleted')} />
              <Stack orientation="horizontal" justify="flex-end">
                <Button
                  variant="primary"
                  text={t('closeButtonLabel')}
                  onClick={handleCompleteOnboarding}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const OnboardingItem = ({
  step,
  id,
  expandedStore,
}: {
  step: StepType;
  id: number;
  expandedStore: SelectionStore;
}) => {
  const {
    name,
    label,
    description,
    isCompleted,
    link,
    isLinkExternal,
    onClick,
    completeStep,
    buttonLabel,
    buttonIcon,
  } = step;
  const isCollapsed = expandedStore.useStore((state) => state[name]);
  return (
    <Stack space={4}>
      <OnboardingItemContainer
        onClick={() => expandedStore.toggle(name)}
        data-clickable={description ? true : false}
        data-completed={isCompleted}
      >
        <Stack orientation="horizontal" space={2} align="center">
          <OnboardingItemStep data-completed={isCompleted}>
            <Text font="semibold12" color="textPrimary" text={isCompleted ? '√' : id + 1} />
          </OnboardingItemStep>
          <Text
            className="step-label"
            font="bold14"
            color={isCompleted ? 'elementGreen100' : 'textHelper'}
            text={label}
          />
        </Stack>
        {description && (
          <IconContainer>
            <IconDropdown color="purple50" rotateDegrees={isCollapsed ? -90 : 0} />
          </IconContainer>
        )}
      </OnboardingItemContainer>
      {description && (
        <Collapse isCollapsed={isCollapsed}>
          <Stack space={4}>
            {description && <Text font="regular14" color="textSecondary" text={description} />}
            {(link || onClick) && (
              <Button
                variant="secondary"
                size="sm"
                fitContent
                text={buttonLabel}
                href={link}
                onClick={onClick ?? completeStep}
                iconLeft={buttonIcon}
                iconRight={isLinkExternal ? <IconOpenNewTab size={16} /> : undefined}
                target={isLinkExternal ? '_blank' : undefined}
              />
            )}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};

const OnboardingItemContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})({
  '&[data-clickable="true"]': {
    cursor: 'pointer',
    // Bleeds out by what it pads in, so clickable and plain rows stay aligned.
    margin: `-${pxToRem(8)}`,
    padding: pxToRem(8),
    borderRadius: pxToRem(4),
    transition: transitionStandard('background-color'),

    '.step-label': {
      transition: transitionStandard('color'),
    },

    '&:hover': {
      backgroundColor: theme.color.uiTrans5,
    },

    // A completed step keeps its green: there the colour is state, not affordance.
    '&:not([data-completed="true"]):hover .step-label': {
      color: theme.color.textPrimary,
    },
  },
});

const OnboardingItemStep = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  height: pxToRem(24),
  width: pxToRem(24),
  color: theme.color.white100,
  backgroundColor: theme.color.elementPurple80,
  '&[data-completed="true"]': {
    backgroundColor: theme.color.elementGreen100,
  },
});

const IconContainer = styled.span({
  display: 'flex',
  transition: transitionStandard('transform'),
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
});

const locale = {
  en: {
    gettingStarted: 'Getting started',
    welcome:
      'Welcome to Journeys! Get familiar with how it works by completing the following tasks',
    createAnAccount: 'Create an account',
    runDemoJourney: 'Run demo journey',
    runDemoJourneyDescription:
      'Send yourself on a demo journey to quickly master the basics of the system',
    runDemoJourneyButtonLabel: 'Open demo journey',
    explorePortal: 'Explore portal',
    explorePortalDescription:
      'Explore the ready-made company portal to discover public forms that initiate journeys',
    explorePortalButtonLabel: 'Open portal',
    exploreTemplates: 'Explore templates',
    exploreTemplatesDescription:
      'Browse our collection of pre-built journey templates for inspiration and faster setup',
    exploreTemplatesButtonLabel: 'Explore templates',
    publishJourney: 'Publish a new journey',
    publishJourneyDescription:
      'Design and launch your own journey by creating a workflow, adding actions, and hitting publish',
    publishJourneyButtonLabel: 'Create new journey',
    inviteTeammate: 'Invite a teammate',
    inviteTeammateDescription:
      'Optionally invite colleagues to explore and build journeys alongside you',
    inviteTeammateButtonLabel: 'Teammates',
    skipButtonLabel: 'Skip getting started guide',
    allStepsCompleted:
      'You have completed all steps in the getting started guide, now go make some workflow magic ✨',
    closeButtonLabel: 'Close getting started guide',
  },
};
